// GPS accuracy band ceilings are an account duty-policy value (2026-08-28 platform lock).
// Keep the defaults, the chip allowlists, and the fail-closed mapper outside the presentation
// module so the policy mapper, the Platform chips, and the Command map cannot drift apart.
//
// SQL twins: account_duty_tracking_policies.gps_accuracy_high_max_meters /
// gps_accuracy_medium_max_meters (20260829120000), graded by app_private.gps_accuracy_band_label.

/** Shipped default: reported radius up to 25 m reads High. */
export const GPS_ACCURACY_HIGH_MAX_DEFAULT_METERS = 25;
/** Shipped default: reported radius up to 100 m reads Medium; beyond it reads Low. */
export const GPS_ACCURACY_MEDIUM_MAX_DEFAULT_METERS = 100;

/** The only High ceilings the writer accepts and the Platform chips offer. */
export const GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS = [10, 15, 25, 50] as const;
/** The only Medium ceilings the writer accepts and the Platform chips offer. */
export const GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS = [50, 75, 100, 150] as const;

export type GpsAccuracyHighMaxMeters = (typeof GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS)[number];
export type GpsAccuracyMediumMaxMeters = (typeof GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS)[number];

/** The pair every band grade reads. Metres are inclusive upper bounds. */
export interface GpsAccuracyBandThresholds {
  highMaxMeters: number;
  mediumMaxMeters: number;
}

export const DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS: GpsAccuracyBandThresholds = Object.freeze({
  highMaxMeters: GPS_ACCURACY_HIGH_MAX_DEFAULT_METERS,
  mediumMaxMeters: GPS_ACCURACY_MEDIUM_MAX_DEFAULT_METERS,
});

function isCeilingCandidate(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * A pair is coherent only when both ceilings are positive and Medium sits strictly above High,
 * which is exactly the table CHECK. Anything else fails closed to the whole default pair rather
 * than patching one side, because a half-repaired pair would grade a number no operator chose.
 */
export function isCoherentGpsAccuracyBandPair(
  highMaxMeters: unknown,
  mediumMaxMeters: unknown,
): boolean {
  return (
    isCeilingCandidate(highMaxMeters)
    && isCeilingCandidate(mediumMaxMeters)
    && mediumMaxMeters > highMaxMeters
  );
}

/**
 * Resolve policy-supplied ceilings into the thresholds the presentation reads. Not snapped to
 * the chip allowlist on purpose: the SQL report grades with whatever the stored row says, and
 * Console must print the same band for the same number, so it accepts any coherent pair and
 * only falls back to 25 / 100 when the pair is unreadable or inverted.
 */
export function gpsAccuracyBandThresholds(
  highMaxMeters: number | null | undefined,
  mediumMaxMeters: number | null | undefined,
): GpsAccuracyBandThresholds {
  if (!isCoherentGpsAccuracyBandPair(highMaxMeters, mediumMaxMeters)) {
    return DEFAULT_GPS_ACCURACY_BAND_THRESHOLDS;
  }
  return { highMaxMeters: highMaxMeters as number, mediumMaxMeters: mediumMaxMeters as number };
}

export function isAllowedGpsAccuracyHighMax(value: number): value is GpsAccuracyHighMaxMeters {
  return (GPS_ACCURACY_HIGH_MAX_ALLOWED_METERS as readonly number[]).includes(value);
}

export function isAllowedGpsAccuracyMediumMax(
  value: number,
): value is GpsAccuracyMediumMaxMeters {
  return (GPS_ACCURACY_MEDIUM_MAX_ALLOWED_METERS as readonly number[]).includes(value);
}

/**
 * The Platform editor's save gate: both chips must be from the allowlists and Medium must be
 * strictly above High. Mirrors the writer's three refusals so the UI never sends a pair the
 * host will bounce.
 */
export function isLegalGpsAccuracyBandChoice(
  highMaxMeters: number,
  mediumMaxMeters: number,
): boolean {
  return (
    isAllowedGpsAccuracyHighMax(highMaxMeters)
    && isAllowedGpsAccuracyMediumMax(mediumMaxMeters)
    && mediumMaxMeters > highMaxMeters
  );
}
